import React from 'react';
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { formatPhone, hasDistance } from './storeLocations';

// Holds a store card's line open when that store has nothing for it, so every card is the same size.
const EMPTY_LINE = ' ';

/**
 * One store in a group's store list: name, street, city, then phone and distance when any store has them.
 *
 * Card heights match because every card renders the same one-line rows (`rows`, from storeCardRows), held open with
 * EMPTY_LINE when a store lacks that detail; height 100% also evens cards sharing a grid row.
 *
 * @param {object} location - one of group.locations, with `distance` once the member's position is known
 * @param {{phone: boolean, distance: boolean}} rows - which optional rows every card shows
 * @param {boolean} [selected] - outlined in the brand color, e.g. the store open on the map
 * @param {() => void} [onSelect] - makes the card a button (the full locations page); omitted, it is plain text
 */
const StoreCard = ({ location, rows, selected = false, onSelect }) => {
  const name = location.friendly_name || location.name;
  const address = location.address || {};
  // The suite rides on the street line, as most stores already keep it ("..., Unit D"), so a suite at one store doesn't
  // open a blank line in every other card.
  const streetLine = [address.address1, address.address2].filter(Boolean).join(', ');
  const cityLine = [address.city, [address.state, address.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  const content = (
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <LocationOnIcon color="primary" sx={{ mt: 0.5 }} />
        {/* minWidth 0 lets the rows shrink so long names and addresses end in an ellipsis. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap title={name}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={streetLine}>
            {streetLine || EMPTY_LINE}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {cityLine || EMPTY_LINE}
          </Typography>
          {rows.phone && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 1 }}>
              {location.phone ? `📞 ${formatPhone(location.phone)}` : EMPTY_LINE}
            </Typography>
          )}
          {rows.distance && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 1, display: 'block' }}>
              {hasDistance(location) ? `${location.distance.toFixed(1)} miles away` : EMPTY_LINE}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  );

  return (
    <Card variant="outlined" sx={{ height: '100%', borderColor: selected ? 'primary.main' : undefined }}>
      {onSelect ? (
        <CardActionArea onClick={onSelect} aria-pressed={selected} sx={{ height: '100%' }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
};

export default StoreCard;
